import { useContext, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  DEFAULT_OUT_OF_AREA_POLICY,
  type ManagedLocation,
  type OutOfAreaPolicy,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import { IcbForm, type IcbFormValues } from "./IcbForm.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Button,
  ButtonIcon,
  MenuFlyout,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export type IcbRow = {
  icb: ManagedLocation;
  /** The active local authorities placed under this ICB. */
  localAuthorityNames: string[];
};

export interface IcbManagementPageInnerProps {
  icbs: IcbRow[];
  /** Resolves true once saved, so the dialog can close; false keeps it open. */
  onCreate: (values: IcbFormValues) => Promise<boolean>;
  onUpdate: (icb: ManagedLocation, values: IcbFormValues) => Promise<boolean>;
  onDelete: (icb: ManagedLocation) => Promise<void>;
}

const POLICY_LABELS: Record<OutOfAreaPolicy, string> = {
  responsible_icb_pays: "Patient's area pays (default)",
  organising_icb_pays: "Organising area pays",
};

const EMPTY_FORM: IcbFormValues = {
  name: "",
  address: "",
  postalCode: "",
  odsCode: "",
  outOfAreaPolicy: DEFAULT_OUT_OF_AREA_POLICY,
};

const toFormValues = (icb: ManagedLocation): IcbFormValues => ({
  name: icb.name,
  address: icb.address,
  postalCode: icb.postalCode,
  odsCode: icb.odsCode ?? "",
  outOfAreaPolicy: icb.outOfAreaPolicy ?? DEFAULT_OUT_OF_AREA_POLICY,
});

function IcbRowInner({
  row,
  onEditClick,
  onDeleteClick,
}: {
  row: IcbRow;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { icb, localAuthorityNames } = row;
  const hasLinkedAuthorities = localAuthorityNames.length > 0;

  const options: MenuOptionsType[] = [
    { name: "Edit", icon: "edit", disabled: false, onClick: onEditClick },
    {
      name: "Delete",
      icon: "delete",
      // Deletion is soft, so a board deleted under its authorities would leave
      // them linked to a retired board.
      disabled: hasLinkedAuthorities,
      onClick: onDeleteClick,
    },
  ];

  return (
    <TableRow>
      <TableCell>{icb.name}</TableCell>
      <TableCell>{icb.odsCode ?? ""}</TableCell>
      <TableCell>
        {POLICY_LABELS[icb.outOfAreaPolicy ?? DEFAULT_OUT_OF_AREA_POLICY]}
      </TableCell>
      <TableCell>
        {hasLinkedAuthorities ? localAuthorityNames.join(", ") : "None"}
      </TableCell>
      <TableCell>
        <Box
          ref={cardRef}
          onClick={(event) => {
            // React bubbles clicks out of the menu's portal, so choosing an
            // option would reopen the menu it has just closed. Only a click on
            // the button itself sits inside this box in the DOM.
            if (
              event.target instanceof Node &&
              event.currentTarget.contains(event.target)
            ) {
              setIsMenuOpen(true);
            }
          }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function IcbManagementPageInner({
  icbs,
  onCreate,
  onUpdate,
  onDelete,
}: IcbManagementPageInnerProps) {
  const [editing, setEditing] = useState<
    { mode: "create" } | { mode: "edit"; icb: ManagedLocation } | null
  >(null);
  const [deleting, setDeleting] = useState<ManagedLocation | null>(null);
  const [isDeleteInFlight, setIsDeleteInFlight] = useState(false);
  const anyLinked = icbs.some((row) => row.localAuthorityNames.length > 0);

  return (
    <Box>
      {deleting && (
        <ConfirmationModal
          message={`Are you sure you want to delete ${deleting.name}?`}
          disabledConfirm={isDeleteInFlight}
          confirmFn={() => {
            setIsDeleteInFlight(true);
            void onDelete(deleting).finally(() => {
              setIsDeleteInFlight(false);
              setDeleting(null);
            });
          }}
          closeFn={() => {
            setDeleting(null);
          }}
        />
      )}

      <PopupDialog
        open={editing !== null}
        onClose={() => {
          setEditing(null);
        }}
      >
        <PopupDialogTitle
          titleText={editing?.mode === "edit" ? "Edit ICB" : "Add ICB"}
          closeDialog={() => {
            setEditing(null);
          }}
        />
        {editing && (
          <IcbForm
            mode={editing.mode}
            initialValues={
              editing.mode === "edit" ? toFormValues(editing.icb) : EMPTY_FORM
            }
            onSubmit={async (values) => {
              const saved =
                editing.mode === "edit"
                  ? await onUpdate(editing.icb, values)
                  : await onCreate(values);
              if (saved) {
                setEditing(null);
              }
            }}
            onCancel={() => {
              setEditing(null);
            }}
          />
        )}
      </PopupDialog>

      <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
        <Typography>
          An ICB groups local authorities and sets who pays when one of its AMHP
          teams organises an assessment for a patient from outside it.
        </Typography>
        <Box sx={{ ml: 2, flexShrink: 0 }}>
          <Button
            variant="contained"
            label="Add ICB"
            endIcon={ButtonIcon.add}
            onClick={() => {
              setEditing({ mode: "create" });
            }}
          />
        </Box>
      </Box>

      {icbs.length === 0 ? (
        <p>No ICBs have been set up</p>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="ICBs">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ODS code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Out-of-area payment policy
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Local authorities
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {icbs.map((row) => (
                <IcbRowInner
                  key={row.icb.id}
                  row={row}
                  onEditClick={() => {
                    setEditing({ mode: "edit", icb: row.icb });
                  }}
                  onDeleteClick={() => {
                    setDeleting(row.icb);
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {anyLinked && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          An ICB can only be deleted once no local authority is placed under it.
        </Typography>
      )}
    </Box>
  );
}

const toIcbLocation = (
  values: IcbFormValues,
): Omit<ManagedLocation, "id" | "status"> => ({ type: "icb", ...values });

/**
 * The reason the server gives for refusing a location. `api` types these
 * responses as `void` whatever the status, so the reason is read from the
 * untyped payload.
 */
const refusalReason = (data: unknown): string | null =>
  typeof data === "object" &&
  data !== null &&
  "reason" in data &&
  typeof data.reason === "string"
    ? data.reason
    : null;

export function IcbManagementPage() {
  const { managedLocations, refetchManagedLocations } = useContext(
    ManagedLocationsContext,
  );

  const icbs = useMemo<IcbRow[]>(() => {
    const namesByIcbId = new Map<string, string[]>();
    for (const location of managedLocations) {
      if (
        location.type === "localAuthority" &&
        location.status === "active" &&
        location.icbId != null
      ) {
        namesByIcbId.set(location.icbId, [
          ...(namesByIcbId.get(location.icbId) ?? []),
          location.name,
        ]);
      }
    }

    return managedLocations
      .filter(
        (location) => location.type === "icb" && location.status === "active",
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((icb) => ({
        icb,
        localAuthorityNames: (namesByIcbId.get(icb.id) ?? []).sort((a, b) =>
          a.localeCompare(b),
        ),
      }));
  }, [managedLocations]);

  /** Run a location request, report its outcome, and say whether it saved. */
  const saved = async (
    request: () => Promise<{ status: number; data: unknown }>,
    messages: { success: string; failure: string },
  ): Promise<boolean> => {
    try {
      const result = await request();
      if (result.status === 204) {
        renderSuccessToast({ message: messages.success });
        refetchManagedLocations();
        return true;
      }
      renderErrorToast({
        message: refusalReason(result.data) ?? messages.failure,
      });
    } catch {
      renderErrorToast({ message: messages.failure });
    }
    return false;
  };

  return (
    <IcbManagementPageInner
      icbs={icbs}
      onCreate={(values) =>
        saved(
          () =>
            api.configuration.addAdminManagedLocation(toIcbLocation(values)),
          { success: "ICB added", failure: "Failed to add the ICB" },
        )
      }
      onUpdate={(icb, values) =>
        saved(
          () =>
            api.configuration.updateAdminManagedLocation(
              icb.id,
              toIcbLocation(values),
            ),
          { success: "ICB updated", failure: "Failed to update the ICB" },
        )
      }
      onDelete={async (icb) => {
        await saved(
          () => api.configuration.deleteAdminManagedLocation(icb.id),
          { success: "ICB deleted", failure: "Failed to delete the ICB" },
        );
      }}
    />
  );
}
